import React, { useMemo } from 'react';
import { type Node as ProseMirrorNode } from '@tiptap/pm/model';
import { mergeAttributes, Node } from '@tiptap/react';
import { type Editor, type NodeViewProps, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { renderLatexToMathML } from '../utils/renderLatexToMathML';

// Options interface for MathInline extension
export interface MathInlineOptions {
  onClick?: (node: ProseMirrorNode, pos: number, editor: Editor) => void | Promise<void>;
}

// React NodeView for rendering math with Temml (MathML output)
const MathNodeView: React.FC<NodeViewProps> = ({
  node,
  selected,
  getPos,
  editor,
  extension
}) => {
  const {
    latex,
    display
  } = node.attrs;
  const html = useMemo(() => renderLatexToMathML(latex || '', !!display), [latex, display]);
  const handleClick = () => {
    const pos = getPos();
    const options = extension.options as MathInlineOptions;
    if (options.onClick && typeof pos === 'number') {
      void options.onClick(node, pos, editor);
    }
  };
  return <NodeViewWrapper as="span" className={`tiptap-mathematics-render tiptap-mathematics-render--editable ${selected ? 'ProseMirror-selectednode' : ''}`} data-type="inline-math" style={{
    display: 'inline-block'
  }} onClick={handleClick} data-sentry-element="NodeViewWrapper" data-sentry-component="MathNodeView" data-sentry-source-file="MathInline.tsx">
      <span dangerouslySetInnerHTML={{
      __html: html
    }} contentEditable={false} />
    </NodeViewWrapper>;
};
export const MathInline = Node.create<MathInlineOptions>({
  name: 'mathInline',
  group: 'inline',
  inline: true,
  selectable: true,
  atom: true,
  addOptions() {
    return {
      onClick: undefined
    };
  },
  addAttributes() {
    return {
      latex: {
        default: '',
        parseHTML: (element: HTMLElement) => element.getAttribute('data-latex') || '',
        renderHTML: (attributes: {
          latex: string;
        }) => ({
          'data-latex': attributes.latex
        })
      },
      display: {
        default: false,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-display') === 'true',
        renderHTML: (attributes: {
          display: boolean;
        }) => attributes.display ? {
          'data-display': 'true'
        } : {}
      }
    };
  },
  parseHTML() {
    return [
    // New format: <math-inline data-latex="">
    {
      tag: 'math-inline'
    },
    // Legacy format: <span data-latex=""> (for extractBareText compatibility)
    {
      tag: 'span[data-latex]',
      getAttrs: (dom: HTMLElement | string) => {
        if (typeof dom === 'string') return false;
        const latex = dom.getAttribute('data-latex');
        return latex ? {
          latex
        } : false;
      }
    }];
  },
  renderHTML({
    HTMLAttributes
  }: {
    HTMLAttributes: Record<string, any>;
  }) {
    // Render as <math-inline> tag for consistency
    return ['math-inline', mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(MathNodeView);
  }
});